import React from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import ModalImage from "react-modal-image";
import { transactionAPI } from "services";
import { formatMoney } from "utils";
import { confirm, TxnActions } from "components";
import useUniversalStore from "store/universalStore";
import useOffcanvasStore from "store/offcanvasStore";
import { toast } from "sonner";

const TxnsTableView = ({
  txns = [],
  openBalance = 0,
  closeBalance = 0,
  dailyCash = { in: "₹ 0", out: "₹ 0" },
  setLoading,
  handleToggle,
  selectedMonth = new Date(),
}) => {
  const { write } = useUniversalStore((state) => state);
  const { openOffcanvas } = useOffcanvasStore();
  const navigate = useNavigate();

  const getVal = (item, key) =>
    typeof item?.get === "function" ? item.get(key) : item?.[key];

  const handleModalOpen = (txnId) => {
    confirm({
      message: "You want to delete this <b>Transaction</b>",
      onConfirm: () => deleteTransaction(txnId),
    });
  };

  const deleteTransaction = async (id) => {
    try {
      setLoading?.(true);
      await transactionAPI.deleteTransaction(id);
      write({
        transactionId: null,
        transactions: await transactionAPI.getTransactionsByDate(selectedMonth),
      });
      handleToggle?.(false);
    } catch (err) {
      console.error("deleteTransaction error in Cashbook:", err);
    } finally {
      setLoading?.(false);
    }
  };

  const editTransaction = (txnId) => {
    write({ transactionId: txnId });
    openOffcanvas("editTxn");
  };

  const openKhataView = (txn) => {
    const account = getVal(txn, "accountId");
    const accountId = account?.id;
    if (!accountId) return;

    if (typeof account.get === "function" && account.get("cancelled")) {
      toast.error("Account is Closed");
      return;
    }
    write({ accountId: accountId });
    navigate("/khatabook");
  };

  const renderAttachment = (attachment) => {
    if (!attachment) {
      return <span className="text-muted"></span>;
    }

    const isPDF = attachment?.includes(".pdf");
    const isImage = attachment?.match(/\.(jpg|jpeg|png|gif|webp)$/i);

    if (isPDF) {
      return (
        <a
          role="button"
          href={attachment}
          target="_blank"
          rel="noreferrer"
          className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1 py-1 px-2 rounded"
          title="Open PDF Attachment"
        >
          <i className="bi bi-file-pdf-fill fs-6" />
          <span className="d-none d-lg-inline fs-xmd-smaller">PDF</span>
        </a>
      );
    }

    if (isImage) {
      return (
        <div
          className="d-inline-flex align-items-center justify-content-center"
          title="Click to view attachment"
        >
          <ModalImage
            small={attachment}
            large={attachment}
            hideDownload={false}
            hideZoom={false}
            alt="Attachment"
            className="table-attachment-img rounded border shadow-sm cursor-pointer"
          />
        </div>
      );
    }

    return (
      <a
        role="button"
        href={attachment}
        target="_blank"
        rel="noreferrer"
        className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1 py-1 px-2 rounded"
        title="Open Attachment"
      >
        <i className="bi bi-paperclip fs-6" />
        <span className="d-none d-lg-inline fs-xmd-smaller">View</span>
      </a>
    );
  };

  return (
    <div className="w-100 p-0 m-0">

      {/* Statement Table */}
      <div
        className="table-responsive scrollbar-design w-100 p-0"
        style={{
          height: "calc(100vh - 200px)",
          maxHeight: "calc(100vh - 200px)",
          overflowY: "auto",
        }}
      >
        <table className="table table-hover align-middle mb-0 khata-statement-table">
          <thead className="table-light position-sticky top-0 z-1 border-bottom">
            <tr>
              <th
                scope="col"
                className="text-start py-3 px-3 text-secondary text-nowrap"
                style={{ width: "130px" }}
              >
                Txn Date
              </th>
              <th
                scope="col"
                className="text-start py-3 px-2 text-secondary text-nowrap"
                style={{ width: "160px" }}
              >
                Account
              </th>
              <th scope="col" className="text-start py-3 px-2 text-secondary">
                Description
              </th>
              <th
                scope="col"
                className="text-end py-3 px-3 text-secondary text-nowrap"
                style={{ width: "140px" }}
              >
                Cash In
              </th>
              <th
                scope="col"
                className="text-end py-3 px-3 text-secondary text-nowrap"
                style={{ width: "140px" }}
              >
                Cash Out
              </th>
              <th
                scope="col"
                className="text-end py-3 px-2 text-secondary text-nowrap"
                style={{ width: "140px" }}
              >
                Balance
              </th>
              <th
                scope="col"
                className="text-center py-3 px-2 text-secondary"
                style={{ width: "50px" }}
              />
            </tr>
          </thead>
          <tbody>
            {txns.length > 0 &&
              txns.map((item, index) => {
                const actualTxn = item?.txn || item;
                const balance =
                  item?.balance !== undefined
                    ? item.balance
                    : actualTxn?.balance ?? 0;
                const txnId = actualTxn?.id || item?.id;
                const amount = getVal(actualTxn, "amount") || 0;
                const detail = getVal(actualTxn, "detail") || "-";
                const date = getVal(actualTxn, "date");
                const attachment = getVal(actualTxn, "attachment");
                const account = getVal(actualTxn, "accountId");
                const isCancelled = getVal(actualTxn, "cancelled");
                const isCashIn = amount > 0;
                const isCashOut = amount < 0;

                const accountName =
                  typeof account?.get === "function"
                    ? account.get("name")
                    : account?.name;

                return (
                  <tr
                    key={txnId || index}
                    className={`border-bottom ${isCancelled ? "opacity-75 bg-light-subtle" : ""
                      }`}
                  >
                    {/* Txn Date */}
                    <td className="text-start py-2 px-3 text-nowrap">
                      <span
                        className={`fs-xmd-smaller ${isCancelled
                          ? "text-muted text-decoration-line-through"
                          : "text-dark"
                          }`}
                      >
                        {dayjs(date).format("DD MMM YYYY")}
                      </span>
                    </td>

                    {/* Account */}
                    <td className="text-start py-2 px-2">
                      {accountName ? (
                        <span
                          onClick={() => !isCancelled && openKhataView(actualTxn)}
                          className={`fw-bold text-dark d-inline-block fs-xmd-smaller select-none ${isCancelled
                            ? "text-muted text-decoration-line-through pe-none"
                            : "cursor-pointer"
                            }`}
                          title={isCancelled ? "" : "Go to Khatabook account"}
                        >
                          {accountName}
                        </span>
                      ) : (
                        <span className="text-muted fs-xmd-smaller">-</span>
                      )}
                    </td>

                    {/* Description */}
                    <td className="text-start py-2 px-2">
                      <div className="d-flex align-items-center flex-wrap">
                        <span
                          className={`text-break me-2 fs-xmd-smaller ${isCancelled
                            ? "text-muted text-decoration-line-through"
                            : "text-dark"
                            }`}
                        >
                          {detail}
                        </span>
                        {renderAttachment(attachment)}
                        {isCancelled && (
                          <span
                            className="badge bg-secondary ms-2"
                            style={{ fontSize: "10px" }}
                          >
                            Cancelled
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Cash In */}
                    <td className="text-end py-2 px-3 text-nowrap">
                      {isCashIn ? (
                        <span
                          className={`fw-semibold ${isCancelled
                            ? "text-muted text-decoration-line-through"
                            : "text-muted"
                            }`}
                        >
                          {formatMoney(amount)}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>

                    {/* Cash Out */}
                    <td className="text-end py-2 px-3 text-nowrap">
                      {isCashOut ? (
                        <span
                          className={`fw-semibold ${isCancelled
                            ? "text-muted text-decoration-line-through"
                            : "text-muted"
                            }`}
                        >
                          {formatMoney(Math.abs(amount))}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>

                    {/* Balance */}
                    <td className="text-end py-2 px-2 text-nowrap">
                      <span
                        className={`fw-semibold ${isCancelled
                          ? "text-muted text-decoration-line-through"
                          : "text-muted"
                          }`}
                      >
                        {formatMoney(balance)}
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="text-center py-2 px-2 text-nowrap">
                      {!isCancelled ? (
                        <TxnActions
                          onEdit={() => editTransaction(txnId)}
                          onDelete={() => handleModalOpen(txnId)}
                        />
                      ) : (
                        <span className="text-muted"></span>
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TxnsTableView;
